import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowMenu } from "web/react/components/filter-redesign/arrow-menu";
import { FilterDialogScreen } from "web/react/components/filter-redesign/filter-dialog/filter-dialog-screen";
import { MenuItemCheckbox } from "web/react/components/filter-redesign/menu-item-checkbox";
import { useActiveFilters, useFilterSync } from "web/react/hooks/filters";
import { UseFiltersSyncResult } from "web/react/hooks/filters/use-filter-sync";
import { gettext } from "web/script/modules/django-i18n";
import { FilterOptionSerializer } from "web/types/serializers";
import { useCategoryFilter } from "./use-category-filter";

interface MultiSelectScreenProps {
    options: FilterOptionSerializer[];
    onSelect: UseFiltersSyncResult["onChange"];
}

function MultiSelectScreen({ onSelect, options }: MultiSelectScreenProps): JSX.Element {
    const { isActive } = useActiveFilters();
    return (
        <>
            {options?.map((subcategory) => (
                <MenuItemCheckbox
                    key={subcategory.url}
                    label={subcategory.label}
                    to={subcategory.url}
                    checked={isActive(subcategory)}
                    onChange={() => onSelect(subcategory.url)}
                />
            ))}
        </>
    );
}

interface SingleSelectScreenProps {
    options: FilterOptionSerializer[];
    onSelect: UseFiltersSyncResult["onChange"];
}

function SingleSelectScreen({ onSelect, options }: SingleSelectScreenProps): JSX.Element {
    const items = options.map(({ id, url, label }) => ({
        id,
        onAction: () => onSelect(url),
        content: label,
    }));

    return <ArrowMenu items={items} />;
}

export function FiltersCategoriesScreen(): JSX.Element {
    const { onChange, isFetching } = useFilterSync();
    const { hasMultiple, options, parent } = useCategoryFilter();
    const navigate = useNavigate();

    const onBack = (): void => {
        parent?.url ? onChange(parent?.url) : navigate("/");
    };

    return (
        <FilterDialogScreen title={gettext("feed.filters.category_title")} onBack={onBack}>
            {!hasMultiple && !isFetching && (
                <SingleSelectScreen onSelect={onChange} options={options} />
            )}
            {hasMultiple && <MultiSelectScreen onSelect={onChange} options={options} />}
        </FilterDialogScreen>
    );
}
